import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Skeleton } from "antd";
import { getCommercialDateAnalysisDetail } from "../../../api/Services";
import { CommercialDateAnalysisDetail } from "../../../types/commercialDateAnalysis";
import ActiveSeasonsNotice from "../../../components/activeseasonsnotice/ActiveSeasonsNotice";
import styles from "./CommercialDateDetail.module.scss";

const formatMoney = (n: number) =>
  "$" + n.toLocaleString("es-UY", { maximumFractionDigits: 0 });

const formatUnits = (n: number) => n.toLocaleString("es-UY");

const formatVar = (v: number | null | undefined, isPp = false) => {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(isPp ? 1 : 0)}${isPp ? " pp" : "%"}`;
};

const kpiSubClass = (v: number | null | undefined) => {
  if (v == null) return styles.varNeutral;
  return v >= 0 ? styles.varPositive : styles.varNegative;
};

const CommercialDateDetailSkeleton: React.FC = () => (
  <div className={styles.wrapper}>
    <div className={styles.topBar}>
      <div className={styles.backRow}>
        <Skeleton.Input active size="small" style={{ width: 180, height: 16 }} />
        <Skeleton.Button active size="small" style={{ width: 120, height: 24 }} />
      </div>
      <Skeleton.Input active style={{ width: 280, height: 32, marginBottom: 8 }} />
      <Skeleton.Input active size="small" style={{ width: 200, height: 16 }} />
    </div>

    <div className={styles.kpiRow}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className={styles.kpiCard}>
          <Skeleton.Input active size="small" style={{ width: "60%", height: 12, marginBottom: 12 }} />
          <Skeleton.Input active style={{ width: "80%", height: 28, marginBottom: 8 }} />
          <Skeleton.Input active size="small" style={{ width: "50%", height: 12 }} />
        </div>
      ))}
    </div>

    <div className={styles.sectionCard}>
      <Skeleton.Input active size="small" style={{ width: 160, height: 16, marginBottom: 16 }} />
      <div className={styles.editionCards}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={styles.editionCard}>
            <Skeleton.Input active style={{ width: 48, height: 22, marginBottom: 12 }} />
            <Skeleton active paragraph={{ rows: 3, width: ["100%", "100%", "100%"] }} title={false} />
          </div>
        ))}
      </div>
    </div>

    <div className={styles.grid2}>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className={styles.sectionCard}>
          <Skeleton.Input active size="small" style={{ width: 180, height: 16, marginBottom: 12 }} />
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      ))}
    </div>

    <div className={styles.grid2}>
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className={styles.sectionCard}>
          <Skeleton.Input active size="small" style={{ width: 160, height: 16, marginBottom: 12 }} />
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ))}
    </div>
  </div>
);

const TableSectionSkeleton: React.FC = () => (
  <div className={styles.sectionCard}>
    <Skeleton.Input active size="small" style={{ width: 180, height: 16, marginBottom: 12 }} />
    <Skeleton active paragraph={{ rows: 8 }} />
  </div>
);

const CommercialDateDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = Number(id);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<CommercialDateAnalysisDetail | null>(null);

  const yearParam = searchParams.get("year");
  const selectedYear = yearParam ? Number(yearParam) : undefined;

  useEffect(() => {
    if (!eventId || Number.isNaN(eventId)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      const result = await getCommercialDateAnalysisDetail(eventId, selectedYear);
      if (cancelled) return;
      if (!result) {
        setError(true);
        setData(null);
      } else {
        setData(result);
        if (!yearParam || Number(yearParam) !== result.selectedYear) {
          setSearchParams({ year: String(result.selectedYear) }, { replace: true });
        }
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, selectedYear, yearParam, setSearchParams]);

  const handleSelectYear = (year: number) => {
    setSearchParams({ year: String(year) });
  };

  if (loading && !data) {
    return <CommercialDateDetailSkeleton />;
  }

  if (error || !data) {
    return (
      <div className={styles.wrapper}>
        <button
          type="button"
          className={styles.backLink}
          onClick={() => navigate("/commercial-analysis/commercial-dates")}
        >
          ← {t("CommercialDateAnalysis-Title")}
        </button>
        <Alert type="error" showIcon message={t("CommercialDateDetail-NotFound")} />
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.topBar}>
        <div className={styles.backRow}>
          <button
            type="button"
            className={styles.backLink}
            onClick={() => navigate("/commercial-analysis/commercial-dates")}
          >
            ← {t("CommercialDateAnalysis-Title")}
          </button>
          <span className={styles.breadcrumbPill}>{t("CommercialDateAnalysis-Diagnostic")}</span>
        </div>
        <h1 className={styles.pageTitle}>{data.name}</h1>
        <p className={styles.subtitle}>{data.dateRangeLabel}</p>
      </div>

      <div className={styles.kpiRow}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>{t("CommercialDateDetail-GrossSales")}</div>
          <div className={styles.kpiValue}>{formatMoney(data.grossSales)}</div>
          <div className={`${styles.kpiSub} ${kpiSubClass(data.grossSalesVarPct)}`}>
            {data.hasPriorEditionComparison
              ? `${formatVar(data.grossSalesVarPct)} ${t("CommercialDateDetail-VsPrevEdition")}`
              : t("CommercialDateDetail-NoPriorComparison")}
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>{t("CommercialDateDetail-Units")}</div>
          <div className={styles.kpiValue}>{formatUnits(data.units)}</div>
          <div className={`${styles.kpiSub} ${kpiSubClass(data.unitsVarPct)}`}>
            {data.hasPriorEditionComparison
              ? `${formatVar(data.unitsVarPct)} ${t("CommercialDateDetail-VsPrevEdition")}`
              : t("CommercialDateDetail-NoPriorComparison")}
          </div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>{t("CommercialDateDetail-UniqueClients")}</div>
          <div className={styles.kpiValue}>{formatUnits(data.uniqueClients)}</div>
          <div className={styles.kpiSubtext}>{t("CommercialDateDetail-UniqueClientsSub")}</div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>{t("CommercialDateDetail-NewClients")}</div>
          <div className={styles.kpiValue}>{formatUnits(data.newClients)}</div>
          <div className={styles.kpiSubtext}>{t("CommercialDateDetail-NewClientsSub")}</div>
        </div>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabel}>{t("CommercialDateDetail-MarginPct")}</div>
          <div className={styles.kpiValue}>{data.marginPct.toFixed(1)}%</div>
          <div className={`${styles.kpiSub} ${kpiSubClass(data.marginVarPp)}`}>
            {data.hasPriorEditionComparison
              ? `${formatVar(data.marginVarPp, true)} ${t("CommercialDateDetail-VsPrevEdition")}`
              : t("CommercialDateDetail-NoPriorComparison")}
          </div>
        </div>
      </div>

      <div className={`${styles.sectionCard} ${loading ? styles.sectionLoading : ""}`}>
        <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-CompareYears")}</h2>
        {loading ? (
          <div className={styles.editionCards}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={styles.editionCard}>
                <Skeleton.Input active style={{ width: 48, height: 22, marginBottom: 12 }} />
                <Skeleton active paragraph={{ rows: 3, width: ["100%", "100%", "100%"] }} title={false} />
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.editionCards}>
            {data.editions.map((ed) => (
              <button
                key={ed.year}
                type="button"
                className={`${styles.editionCard} ${
                  data.selectedYear === ed.year ? styles.editionCardActive : ""
                }`}
                onClick={() => handleSelectYear(ed.year)}
              >
                <div className={styles.editionYear}>{ed.year}</div>
                <div className={styles.editionRow}>
                  <span>{t("CommercialDateDetail-Sale")}</span>
                  <strong>{formatMoney(ed.grossSales)}</strong>
                </div>
                <div className={styles.editionRow}>
                  <span>{t("CommercialDateDetail-Units")}</span>
                  <strong>{formatUnits(ed.units)}</strong>
                </div>
                <div className={styles.editionRow}>
                  <span>{t("CommercialDateDetail-Margin")}</span>
                  <strong>{ed.marginPct.toFixed(1)}%</strong>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className={`${styles.grid2} ${loading ? styles.sectionLoading : ""}`}>
        {loading ? (
          <>
            <TableSectionSkeleton />
            <TableSectionSkeleton />
          </>
        ) : (
          <>
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-PerformanceByStore")}</h2>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t("CommercialDateDetail-Store")}</th>
                <th>{t("CommercialDateDetail-Sale")}</th>
                <th>{t("CommercialDateDetail-Units")}</th>
                <th>{t("CommercialDateDetail-Tickets")}</th>
                <th>{t("CommercialDateDetail-AvgTicket")}</th>
                <th>{t("CommercialDateDetail-Margin")}</th>
                <th>{t("CommercialDateDetail-EventSalesPct")}</th>
              </tr>
            </thead>
            <tbody>
              {data.stores.map((row) => (
                <tr key={row.branchOfficeId}>
                  <td>{row.store}</td>
                  <td>{formatMoney(row.sales)}</td>
                  <td>{formatUnits(row.units)}</td>
                  <td>{formatUnits(row.tickets)}</td>
                  <td>{formatMoney(row.avgTicket)}</td>
                  <td>{row.marginPct.toFixed(1)}%</td>
                  <td>{row.eventSalesPct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-PerformanceByChannel")}</h2>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t("CommercialDateDetail-Channel")}</th>
                <th>{t("CommercialDateDetail-Sale")}</th>
                <th>{t("CommercialDateDetail-Units")}</th>
                <th>{t("CommercialDateDetail-Tickets")}</th>
                <th>{t("CommercialDateDetail-AvgTicket")}</th>
                <th>{t("CommercialDateDetail-Margin")}</th>
                <th>{t("CommercialDateDetail-EventSalesPct")}</th>
              </tr>
            </thead>
            <tbody>
              {data.channels.map((row) => (
                <tr key={row.channelId}>
                  <td>{row.channel}</td>
                  <td>{formatMoney(row.sales)}</td>
                  <td>{formatUnits(row.units)}</td>
                  <td>{formatUnits(row.tickets)}</td>
                  <td>{formatMoney(row.avgTicket)}</td>
                  <td>{row.marginPct.toFixed(1)}%</td>
                  <td>{row.eventSalesPct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          </>
        )}
      </div>

      {loading ? (
        <>
          <div className={styles.grid2}>
            <TableSectionSkeleton />
            <TableSectionSkeleton />
          </div>
          <div className={styles.grid2}>
            <TableSectionSkeleton />
            <TableSectionSkeleton />
          </div>
          <TableSectionSkeleton />
        </>
      ) : (
        <>
      <div className={styles.grid2}>
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-TopProducts")}</h2>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t("CommercialDateDetail-Product")}</th>
                <th>{t("CommercialDateDetail-Category")}</th>
                <th>{t("CommercialDateDetail-Units")}</th>
                <th>{t("CommercialDateDetail-Sale")}</th>
                <th>{t("CommercialDateDetail-Margin")}</th>
                <th>{t("CommercialDateDetail-EventSalesPct")}</th>
              </tr>
            </thead>
            <tbody>
              {data.topProducts.map((row) => (
                <tr key={row.articleId}>
                  <td>
                    <div>{row.name}</div>
                    {row.externalId && (
                      <div className={styles.externalId}>{row.externalId}</div>
                    )}
                  </td>
                  <td>{row.categoryPath}</td>
                  <td>{formatUnits(row.units)}</td>
                  <td>{formatMoney(row.sales)}</td>
                  <td>{row.marginPct.toFixed(1)}%</td>
                  <td>{row.eventSalesPct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-BottomProducts")}</h2>
          <ActiveSeasonsNotice />
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t("CommercialDateDetail-Product")}</th>
                <th>{t("CommercialDateDetail-Category")}</th>
                <th>{t("CommercialDateDetail-Units")}</th>
                <th>{t("CommercialDateDetail-Sale")}</th>
              </tr>
            </thead>
            <tbody>
              {data.bottomProducts.map((row) => (
                <tr key={row.articleId}>
                  <td>
                    <div>{row.name}</div>
                    {row.externalId && (
                      <div className={styles.externalId}>{row.externalId}</div>
                    )}
                  </td>
                  <td>{row.categoryPath}</td>
                  <td>{formatUnits(row.units)}</td>
                  <td>{formatMoney(row.sales)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.grid2}>
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-TopCategories")}</h2>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t("CommercialDateDetail-Category")}</th>
                <th>{t("CommercialDateDetail-Sale")}</th>
                <th>{t("CommercialDateDetail-Units")}</th>
                <th>{t("CommercialDateDetail-Margin")}</th>
                <th>{t("CommercialDateDetail-EventSalesPct")}</th>
              </tr>
            </thead>
            <tbody>
              {data.topCategories.map((row) => (
                <tr key={row.categoryId}>
                  <td>{row.categoryPath}</td>
                  <td>{formatMoney(row.sales)}</td>
                  <td>{formatUnits(row.units)}</td>
                  <td>{row.marginPct.toFixed(1)}%</td>
                  <td>{row.eventSalesPct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-BottomCategories")}</h2>
          <ActiveSeasonsNotice />
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>{t("CommercialDateDetail-Category")}</th>
                <th>{t("CommercialDateDetail-Sale")}</th>
                <th>{t("CommercialDateDetail-Units")}</th>
              </tr>
            </thead>
            <tbody>
              {data.bottomCategories.map((row) => (
                <tr key={row.categoryId}>
                  <td>{row.categoryPath}</td>
                  <td>{formatMoney(row.sales)}</td>
                  <td>{formatUnits(row.units)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.sectionCard}>
        <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-ActivePromotions")}</h2>
        <table className={styles.dataTable}>
          <thead>
            <tr>
              <th>{t("CommercialDateDetail-Promotion")}</th>
              <th>{t("CommercialDateDetail-Type")}</th>
              <th>{t("CommercialDateDetail-Products")}</th>
              <th>{t("CommercialDateDetail-Sale")}</th>
              <th>{t("CommercialDateDetail-Units")}</th>
              <th>{t("CommercialDateDetail-Margin")}</th>
              <th>{t("CommercialDateDetail-Roi")}</th>
            </tr>
          </thead>
          <tbody>
            {data.promotions.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyRow}>
                  {t("CommercialDateDetail-NoPromotions")}
                </td>
              </tr>
            ) : (
              data.promotions.map((row) => (
                <tr key={row.promotionId}>
                  <td>{row.name}</td>
                  <td>{row.type}</td>
                  <td>{row.allProducts ? t("CommercialDateDetail-AllProducts") : "—"}</td>
                  <td>{formatMoney(row.sales)}</td>
                  <td>{formatUnits(row.units)}</td>
                  <td>{row.marginPct.toFixed(1)}%</td>
                  <td>{row.roi != null ? `${row.roi.toFixed(1)}%` : "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className={styles.grid2}>
        <div className={`${styles.sectionCard} ${styles.forecastPending}`}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-ForecastProducts")}</h2>
          <Alert type="info" showIcon message={t("CommercialDateDetail-ForecastPending")} />
        </div>
        <div className={`${styles.sectionCard} ${styles.forecastPending}`}>
          <h2 className={styles.sectionTitle}>{t("CommercialDateDetail-ForecastCategories")}</h2>
          <Alert type="info" showIcon message={t("CommercialDateDetail-ForecastPending")} />
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default CommercialDateDetailPage;
